<template>
  <Card noBorder>
    <SectionHeader
      title="Remote Assist"
    >
      {{ $options.instructions.general }}
      <Link
        :href="$options.instructions.knowMoreUrl"
        :isDarkText="false"
        :isMatchText="false"
        isExternal
        target="_blank"
        text="Learn more about Remote Assist"
      />
    </SectionHeader>
    <Card :class="$style.card">
      <template v-slot:header>
        <header
          :class="$style.cardHeader"
        >
          <ToggleSwitch
            v-model="remoteAssistEnabled"
            :data-test-id="$testId('toggle-remote-assist-service')"
            :isDisabled="readonly"
            :label="$options.switchLabels.remote"
            :labelOn="$options.switchLabels.remoteOn"
          />
        </header>
      </template>
      <template v-slot:body>
        <ParagraphBody
          :class="$style.description"
          data-test-id="RemoteAssist__remoteAssistText"
        >
          {{ $options.instructions.remoteAssistLabel }}
        </ParagraphBody>
        <Card
          v-if="remoteAssistEnabled && shouldShowSilentAssistToggle"
          :hasNoBorder="true"
        >
          <header
            :class="$style.cardHeader"
          >
            <ToggleSwitch
              v-model="silentEnabled"
              :class="$style.silentAssistLabel"
              :isDisabled="readonly"
              :label="$options.switchLabels.silent"
              :labelOn="$options.switchLabels.silentOn"
            />
          </header>
          <br>
          <ParagraphBody
            :class="$style.description"
            data-test-id="SilentAssist__silentAssistText"
          >
            {{ $options.instructions.silentAssistDesc }}
          </ParagraphBody>
        </Card>
        <Select
          :data-test-id="$testId('select-remote-assist-session-timeout')"
          :isDisabled="(!remoteAssistEnabled || readonly)"
          :isHelperTextMarkdown="false"
          label="Session Timeout Duration"
          :options="optionDropdown"
          placeholder="Select duration"
          :required="true"
          :value="selectedOption"
          @input="updateOption"
        />
      </template>
    </Card>
    <Card :class="$style.card">
      <template v-slot:header>
        <header
          :class="$style.cardHeader"
        >
          <ToggleSwitch
            v-model="surveyEnabled"
            :data-test-id="$testId('toggle-remote-assist-survey')"
            :isDisabled="readonly"
            :label="$options.switchLabels.survey"
            :labelOn="$options.switchLabels.surveyOn"
          />
        </header>
      </template>
      <template v-slot:body>
        <div>
          <ParagraphBody
            :class="$style.description"
            data-test-id="survey__surveyText"
          >
            {{ $options.instructions.surveyLabel }}
          </ParagraphBody>
        </div>
      </template>
    </Card>
  </Card>
</template>

<script>
import {
  Card,
  Link,
  ParagraphBody,
  Select,
  ToggleSwitch,
} from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import RemoteAssistApi from 'client/api/v2/RemoteAssistApi';
import SectionHeader from 'client/settings/components/SectionHeader.vue';

/*
 * toggle switch disabled value need to be changed in GA
 */

const instructions = {
  general: 'Remote Assist allows remote access and control of devices for maintenance and assistance.',
  knowMoreUrl: KbArticleUrls.remoteAssist,
  remoteAssistLabel: 'Configure the maximum duration for Remote Assist sessions. Once the selected duration is met the session will timeout.',
  silentAssistDesc: 'Allow remote sessions and provide device support without the end-user being present.',
  surveyLabel: 'This allows JumpCloud to collect survey data for service enhancement.',
};

const remoteLabel = 'Remote Assist Service';
const silentLabel = 'Silent Assist';
const surveyLabel = 'Experience Survey';

const switchLabels = {
  remote: `${remoteLabel} Off`,
  remoteOn: `${remoteLabel} On`,
  silent: `${silentLabel} Off`,
  silentOn: `${silentLabel} On`,
  survey: `${surveyLabel} Off`,
  surveyOn: `${surveyLabel} On`,
};

export default {
  name: 'SectionRemoteAssist',

  components: {
    Card,
    Link,
    ParagraphBody,
    Select,
    SectionHeader,
    ToggleSwitch,
  },

  props: {
    readonly: Boolean,
  },

  data() {
    return {
      feedbackEnabled: true,
      rasEnabled: true,
      selectedOption: '120',
      sessionTimeout: 120,
      silentAssistEnabled: false,
    };
  },

  computed: {
    optionDropdown() {
      const arr = [15, 30, 45, 60, 75, 90, 105, 120]
        .map(minutes => ({
          name: `${minutes} minutes`,
          value: `${minutes}`,
        }));
      return arr;
    },

    remoteAssistEnabled: {
      get() {
        return this.rasEnabled;
      },
      set(newValue) {
        this.rasEnabled = newValue;
        if (!this.rasEnabled) { this.silentAssistEnabled = false; }
        this.$emit('changeRemoteAssistSettings', this.feedbackEnabled, this.rasEnabled, this.sessionTimeout, this.silentAssistEnabled);
      },
    },

    shouldShowSilentAssistToggle() {
      // eslint-disable-next-line no-console
      return this.$hasFeatureFlag(this.$featureFlags.silentAssist);
    },

    silentEnabled: {
      get() {
        return this.silentAssistEnabled;
      },
      set(newValue) {
        this.silentAssistEnabled = newValue;
        this.$emit('changeRemoteAssistSettings', this.feedbackEnabled, this.rasEnabled, this.sessionTimeout, this.silentAssistEnabled);
      },
    },

    surveyEnabled: {
      get() {
        return this.feedbackEnabled;
      },
      set(newValue) {
        this.feedbackEnabled = newValue;
        this.$emit('changeRemoteAssistSettings', this.feedbackEnabled, this.rasEnabled, this.sessionTimeout, this.silentAssistEnabled);
      },
    },
  },

  instructions,
  switchLabels,

  ...mapState('FeatureFlagsModule', {
    silentAssist: state => state.betaFeatures.silentAssist,
  }),

  beforeMount() {
    this.fetchRemoteAssistConfig();
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    async fetchRemoteAssistConfig() {
      try {
        const data = await RemoteAssistApi.getConfig();
        this.rasEnabled = data.enabled;
        this.feedbackEnabled = data.feedbackEnabled;
        this.sessionTimeout = data.sessionTimeout / 60;
        this.selectedOption = `${this.sessionTimeout}`;
        this.silentAssistEnabled = data.silentAssistEnabled;
      } catch (err) {
        const message = get(err, 'response.data.message', 'Unable to fetch Remote Assist Settings');
        this.triggerErrorNotification({ title: 'Error', message });
      }
    },

    updateOption(option) {
      this.selectedOption = option;
      this.sessionTimeout = Number(this.selectedOption);
      this.$emit('changeRemoteAssistSettings', this.feedbackEnabled, this.rasEnabled, this.sessionTimeout, this.silentAssistEnabled);
    },
  },
};
</script>

<style module scoped>
.card {
  --text-max-width: 75ch;
}

.card:not(:last-child) {
  margin-bottom: var(--jc-spacer);
}

.cardHeader {
  --switch-label-align: center;
  --switch-label-size: var(--jcBodyLarge);
  --switch-label-weight: 700;

  display: flex;
  flex-wrap: wrap;
  gap: var(--jc-spacer);
}

.description {
  max-width: var(--text-max-width);
}

.silentAssistLabel {
  max-width: 20ch;
}
</style>
